import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import AsyncSelect from "react-select/async";
import { useField } from "formik";
import { Layers } from "lucide-react";
import SectionCard from "../ui/SectionCard";
import Field from "../ui/Field";
import { useFormOptions } from "../FormOptionsContext";
import { useSingleIdField } from "../useSelectField";
import { useProductSearch } from "../useProductSearch";
import { createSelectStyles, selectTheme } from "../ui/selectStyles";

const richSelectStyles = (invalid) => {
  const base = createSelectStyles({ invalid });
  return {
    ...base,
    option: (provided, state) => ({
      ...base.option(provided, state),
      padding: 0,
    }),
  };
};

const ProductOptionRow = ({ option }) => {
  const { sizeOptions, colorOptions } = useFormOptions();
  const sizeLabel = useMemo(
    () => sizeOptions.find((s) => s.value === option?.data?.size)?.label,
    [sizeOptions, option],
  );
  const colorLabel = useMemo(
    () => colorOptions.find((c) => c.value === option?.data?.color)?.label,
    [colorOptions, option],
  );
  const meta = [sizeLabel, colorLabel, option?.data?.barcode]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          flex: "0 0 36px",
          borderRadius: 6,
          background: "#f1f4f6",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {option?.data?.image ? (
          <img
            src={option.data.image}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span style={{ color: "#b5c0c8", fontSize: 11 }}>—</span>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "#25343f",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {option?.label || `Product #${option?.value}`}
        </div>
        {meta && (
          <div
            style={{
              fontSize: 11,
              color: "#8a9ba8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {meta}
          </div>
        )}
      </div>
    </div>
  );
};

const formatProductOptionLabel = (option, { context }) => {
  if (context === "value") {
    const barcode = option?.data?.barcode;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span>{option?.label || `Product #${option?.value}`}</span>
        {barcode && (
          <span style={{ fontSize: 10, color: "#8a9ba8" }}>· {barcode}</span>
        )}
      </span>
    );
  }
  return <ProductOptionRow option={option} />;
};

const useSingleIdMultiField = (name, idKey) => {
  const [, meta, helpers] = useField(name);

  const currentIds = useMemo(
    () =>
      Array.isArray(meta.value)
        ? meta.value.map((row) => row?.[idKey]).filter((v) => v != null)
        : [],
    [meta.value, idKey],
  );

  const setIds = (ids) => {
    helpers.setValue(ids.map((id) => ({ [idKey]: id })));
  };

  return {
    ids: currentIds,
    setIds,
    onBlur: () => helpers.setTouched(true),
    meta,
  };
};

const VariationSection = () => {
  const {
    colorOptions,
    sizeOptions,
    productOptions,
    linkedProductOptions,
    excludeProductId,
  } = useFormOptions();
  const colorSel = useSingleIdField("color", colorOptions);
  const sizeSel = useSingleIdField("size", sizeOptions);
  const [nameField] = useField("name");

  const productOptionsField = useSingleIdMultiField(
    "product_options",
    "product_option_id",
  );
  const { loadOptions: loadProductOptions, defaultOptions: productOptionDefaults } =
    useProductSearch({
      excludeProductId,
      suggestByName: nameField.value,
    });

  // Display data for selected chips. Hydrated from (a) this product's own
  // already-linked siblings — authoritative, and the only source for links to
  // products that aren't on page 1 of the global list — and (b) the page-1
  // product list the form loads. Grown as the user picks new items from the
  // async dropdown.
  const [optionMap, setOptionMap] = useState(() => new Map());

  const seedOptionMap = useCallback((options) => {
    if (!options?.length) return;
    setOptionMap((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const o of options) {
        if (!next.has(o.value)) {
          next.set(o.value, { ...o, data: o.data ?? {} });
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  useEffect(() => {
    seedOptionMap(linkedProductOptions);
  }, [linkedProductOptions, seedOptionMap]);

  useEffect(() => {
    seedOptionMap(productOptions);
  }, [productOptions, seedOptionMap]);

  const selectValue = useMemo(
    () =>
      productOptionsField.ids.map(
        (id) =>
          optionMap.get(id) || {
            value: id,
            label: `Product #${id}`,
            data: {},
          },
      ),
    [productOptionsField.ids, optionMap],
  );

  const handleProductOptionsChange = (selected) => {
    const arr = Array.isArray(selected) ? selected : [];
    setOptionMap((prev) => {
      const next = new Map(prev);
      for (const o of arr) {
        if (o?.value != null) next.set(o.value, o);
      }
      return next;
    });
    productOptionsField.setIds(arr.map((o) => o.value));
  };

  const productOptionsInvalid =
    productOptionsField.meta.touched && !!productOptionsField.meta.error;

  return (
    <SectionCard
      icon={<Layers size={16} />}
      title="Variation"
      subtitle="Optional physical attributes used for filtering and display."
      id="variation"
    >
      <div className="pf-grid pf-grid--2">
        <Field label="Color" error={colorSel.error}>
          <ReactSelect
            inputId="color"
            placeholder="Select a color"
            isClearable
            options={colorOptions}
            value={colorSel.value}
            onChange={colorSel.onChange}
            onBlur={colorSel.onBlur}
            styles={createSelectStyles({ invalid: colorSel.isInvalid })}
            theme={selectTheme}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
          />
        </Field>
        <Field label="Size" error={sizeSel.error}>
          <ReactSelect
            inputId="size"
            placeholder="Select a size"
            isClearable
            options={sizeOptions}
            value={sizeSel.value}
            onChange={sizeSel.onChange}
            onBlur={sizeSel.onBlur}
            styles={createSelectStyles({ invalid: sizeSel.isInvalid })}
            theme={selectTheme}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
          />
        </Field>
        <Field
          label="Product Options"
          hint="Search by name or barcode to link sibling products (different size, colour, variant)."
          error={
            productOptionsField.meta.touched
              ? productOptionsField.meta.error
              : null
          }
        >
          <AsyncSelect
            inputId="product_options"
            isMulti
            cacheOptions
            defaultOptions={productOptionDefaults}
            loadOptions={loadProductOptions}
            value={selectValue}
            onChange={handleProductOptionsChange}
            onBlur={productOptionsField.onBlur}
            placeholder="Type a product name or barcode…"
            noOptionsMessage={({ inputValue }) =>
              inputValue
                ? "No products match"
                : "Type a product name or barcode to search"
            }
            loadingMessage={() => "Searching…"}
            formatOptionLabel={formatProductOptionLabel}
            styles={richSelectStyles(productOptionsInvalid)}
            theme={selectTheme}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
          />
        </Field>
      </div>
    </SectionCard>
  );
};

export default React.memo(VariationSection);
